/**
 * DateRange — start + end date picker with shortcut presets
 *
 * @example
 * <DateRange
 *   value={range}        // { from: Date, to: Date }
 *   onChange={setRange}
 *   shortcuts={['today', 'last7', 'last30', 'thisMonth']}
 * />
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { CalendarIcon, X } from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth, isValid } from 'date-fns';
import { cn } from '@/lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';

// ─── Shortcut presets ─────────────────────────────────────────────────────────
const SHORTCUTS = {
  today: (t) => ({
    label: t('admin.date_today'),
    range: () => { const d = new Date(); return { from: d, to: d }; },
  }),
  last7: (t) => ({
    label: t('admin.date_last7'),
    range: () => ({ from: subDays(new Date(), 6), to: new Date() }),
  }),
  last30: (t) => ({
    label: t('admin.date_last30'),
    range: () => ({ from: subDays(new Date(), 29), to: new Date() }),
  }),
  thisMonth: (t) => ({
    label: t('admin.date_this_month'),
    range: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }),
  }),
  lastMonth: (t) => ({
    label: t('admin.date_last_month'),
    range: () => {
      const first = startOfMonth(subDays(startOfMonth(new Date()), 1));
      return { from: first, to: endOfMonth(first) };
    },
  }),
};

function formatRange(range, t) {
  if (!range?.from) return t('admin.date_placeholder');
  const from = isValid(range.from) ? format(range.from, 'dd/MM/yyyy') : '';
  const to = range.to && isValid(range.to) ? format(range.to, 'dd/MM/yyyy') : '';
  if (!to || from === to) return from;
  return `${from} — ${to}`;
}

export default function DateRange({ value, onChange, shortcuts, disabled, className }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  const handleSelect = (selected) => {
    onChange?.(selected);
    if (selected?.from && selected?.to) setOpen(false);
  };

  const handleShortcut = (key) => {
    const s = SHORTCUTS[key]?.(t);
    if (!s) return;
    const range = s.range();
    onChange?.(range);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.({ from: null, to: null });
  };

  const hasValue = value?.from && isValid(value.from);

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full justify-start rounded-xl border-2 border-[var(--border-light)] h-9 px-3 font-semibold text-sm bg-white',
            'hover:border-[var(--brand-primary)]',
            !hasValue && 'text-[var(--text-muted)]',
            className,
          )}
          aria-label={t('admin.date_range_label')}
        >
          <CalendarIcon size={15} className="mr-2 text-[var(--text-muted)]" />
          <span className="flex-1 text-left truncate">{formatRange(value, t)}</span>
          {hasValue && (
            <button
              onClick={handleClear}
              className="ml-2 text-[var(--text-muted)] hover:text-[var(--brand-danger)]"
              aria-label={t('admin.date_clear')}
            >
              <X size={13} />
            </button>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 rounded-xl border-2 border-[var(--border-light)] shadow-[0_4px_0_0_var(--border-light)]"
        align="start"
      >
        {/* Shortcuts */}
        {shortcuts && shortcuts.length > 0 && (
          <div className="flex flex-wrap gap-1.5 p-3 border-b border-[var(--border-light)]">
            {shortcuts.map((key) => {
              const s = SHORTCUTS[key]?.(t);
              if (!s) return null;
              return (
                <button
                  key={key}
                  onClick={() => handleShortcut(key)}
                  className="px-2.5 py-1 rounded-lg text-xs font-bold border-2 border-[var(--border-light)] bg-white hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] transition-colors"
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        )}
        {/* Calendar */}
        <Calendar
          mode="range"
          selected={value}
          onSelect={handleSelect}
          numberOfMonths={2}
          className="p-3"
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

DateRange.propTypes = {
  value: PropTypes.shape({
    from: PropTypes.instanceOf(Date),
    to: PropTypes.instanceOf(Date),
  }),
  onChange: PropTypes.func,
  shortcuts: PropTypes.arrayOf(
    PropTypes.oneOf(['today', 'last7', 'last30', 'thisMonth', 'lastMonth']),
  ),
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

DateRange.defaultProps = {
  shortcuts: ['today', 'last7', 'last30', 'thisMonth'],
  disabled: false,
};
